// ============================================================
// TERROR ENGINE — SPRITE MANIFEST
// Every player and companion sprite URL, defined once.
// All zones import from here. Add a new frame? Add it here.
// _b walk frames disabled for now — all point to _a.
// When walk animation is ready: swap _b entries to real files.
// ============================================================

import { CDN } from "./CDN";

// ── Player sprites ────────────────────────────────────────────
export const PLAYER_SPRITES = {
  // Female — directional walk
  player_f_down_a:  `${CDN}/player_f_down_a.webp`,
  player_f_down_b:  `${CDN}/player_f_down_a.webp`,   // _b disabled
  player_f_up_a:    `${CDN}/player_f_up_a.webp`,
  player_f_up_b:    `${CDN}/player_f_up_a.webp`,     // _b disabled
  player_f_left_a:  `${CDN}/player_f_left_a.webp`,
  player_f_left_b:  `${CDN}/player_f_left_a.webp`,   // _b disabled
  player_f_right_a: `${CDN}/player_f_right_a.webp`,
  player_f_right_b: `${CDN}/player_f_right_a.webp`,  // _b disabled
  // Female — idle / bored / front
  player_f_front_a: `${CDN}/player_f_front_a.webp`,
  player_f_idle_a:  `${CDN}/player_f_idle_a.webp`,
  player_f_idle_b:  `${CDN}/player_f_idle_b.webp`,   // ✓ two female idle frames
  player_f_bored_a: `${CDN}/player_f_bored_a.webp`,
  player_f_default: `${CDN}/player_f_front_a.webp`,

  // Male — directional walk
  player_m_down_a:  `${CDN}/player_m_down_a.webp`,
  player_m_down_b:  `${CDN}/player_m_down_a.webp`,   // _b disabled
  player_m_up_a:    `${CDN}/player_m_up_a.webp`,
  player_m_up_b:    `${CDN}/player_m_up_a.webp`,     // _b disabled
  player_m_left_a:  `${CDN}/player_m_left_a.webp`,
  player_m_left_b:  `${CDN}/player_m_left_a.webp`,   // _b disabled
  player_m_right_a: `${CDN}/player_m_right_a.webp`,
  player_m_right_b: `${CDN}/player_m_right_a.webp`,  // _b disabled
  // Male — idle / bored / front (one frame — reuse _a)
  player_m_front_a: `${CDN}/player_m_idle_a.webp`,
  player_m_idle_a:  `${CDN}/player_m_idle_a.webp`,
  player_m_idle_b:  `${CDN}/player_m_idle_a.webp`,   // no second male idle frame yet
  player_m_bored_a: `${CDN}/player_m_idle_a.webp`,
  player_m_default: `${CDN}/player_m_idle_a.webp`,
};

// ── Companions ────────────────────────────────────────────────
// Explicit overrides only. Anything NOT listed here is derived on the
// fly as `${CDN}/${companionId}.webp` (see getCompanionSprite). That
// means a brand-new companion needs ZERO entries here — drop its art
// at terror-assets/<id>.webp and it appears everywhere at once.
//
// `default` is the universal safety net: if a companion's own art file
// doesn't exist yet, the <img> onError swaps to this (J'ynxi the
// prototype), so a room never renders a broken sprite. THIS is why the
// companion looked like J'ynxi in every room before — there was no
// per-id path, so every id fell through to the default. Now each id
// gets its OWN url first; J'ynxi is only the fallback-of-last-resort.
export const COMPANION_SPRITES = {
  default: `${CDN}/jynxi.webp`,
  jynxi:   `${CDN}/jynxi.webp`,
};

// The url every un-arted companion falls back to (used by <img> onError).
export const COMPANION_FALLBACK = COMPANION_SPRITES.default;

// ── Zone backgrounds ──────────────────────────────────────────
export const ZONE_BACKGROUNDS = {
  plaza:        `${CDN}/p_center.webp`,
  workshop:     `${CDN}/p_forge.webp`,
  faction_hall: `${CDN}/p_alliance.webp`,
};

// ── Zone NPC / rep sprites ────────────────────────────────────
export const NPC_SPRITES = {
  ser_vael: `${CDN}/p_nihil.webp`,
  calyx:    `${CDN}/p_omnia.webp`,
};

// ── Helpers ───────────────────────────────────────────────────

/**
 * Resolve directional player sprite.
 * @param {string} gender  "f" | "m"
 * @param {number} facingX -1 | 0 | 1
 * @param {number} facingY -1 | 0 | 1
 * @param {boolean} walkFrame  true = _b frame, false = _a frame
 */
export function getPlayerSprite(gender, facingX, facingY, walkFrame) {
  const g     = gender === "f" ? "f" : "m";
  const frame = walkFrame ? "b" : "a";
  let dir = "down";
  if (facingY === -1)      dir = "up";
  else if (facingY === 1)  dir = "down";
  else if (facingX === -1) dir = "left";
  else if (facingX === 1)  dir = "right";
  return (
    PLAYER_SPRITES[`player_${g}_${dir}_${frame}`] ||
    PLAYER_SPRITES[`player_${g}_${dir}_a`]        ||
    PLAYER_SPRITES[`player_${g}_default`]
  );
}

/**
 * Resolve idle sprite, gender-aware.
 * @param {string}  gender    "f" | "m"
 * @param {boolean} idleFrame true = _b (female only)
 */
export function getIdleSprite(gender, idleFrame) {
  const g = gender === "f" ? "f" : "m";
  return (
    PLAYER_SPRITES[`player_${g}_idle_${g === "f" && idleFrame ? "b" : "a"}`] ||
    PLAYER_SPRITES[`player_${g}_default`]
  );
}

/**
 * Companion sprite by id.
 * Priority:
 *   1. explicit override in COMPANION_SPRITES (e.g. jynxi)
 *   2. derived per-companion CDN art: `${CDN}/${id}.webp`
 *   3. (at render time) <img> onError → COMPANION_FALLBACK (J'ynxi)
 *
 * Returning the derived url for unknown ids — rather than the J'ynxi
 * default — is what lets each companion show its OWN art the moment the
 * file lands on the CDN, with no code change. Until that art exists the
 * url 404s and the renderer's onError falls back to J'ynxi gracefully.
 */
export function getCompanionSprite(companionId) {
  if (!companionId) return COMPANION_SPRITES.default;
  if (COMPANION_SPRITES[companionId]) return COMPANION_SPRITES[companionId];
  return `${CDN}/${companionId}.webp`;
}

/** The url to swap to when a companion's own art fails to load. */
export function getCompanionFallback() {
  return COMPANION_FALLBACK;
}
